// frontend/src/components/chat/GroupInfoPanel.jsx
// PHASE 6: Side panel for viewing and managing group chat details
// Shows participants, allows admin actions like rename, add/remove members

import React, { useState, useCallback } from "react";
import { useChat } from "../../contexts/ChatContext";

const GroupInfoPanel = ({ conversation, onClose }) => {
  const {
    groupParticipants,
    isLoadingParticipants,
    currentUserId,
    updateGroup,
    addGroupParticipants,
    removeGroupParticipant,
    leaveGroup,
    updateParticipantRole,
    toggleGroupMute,
    fetchChatableUsers,
    chatableUsers,
    isUserOnline,
  } = useChat();

  // UI State
  const [activeTab, setActiveTab] = useState("members"); // 'members', 'settings', 'add'
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(conversation.group_name || "");
  const [editDescription, setEditDescription] = useState(
    conversation.group_description || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Add members state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Confirmation modals
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Get current user's role in the group
  const currentUserParticipant = groupParticipants.find(
    (p) => p.user_id === currentUserId
  );
  const currentUserRole = currentUserParticipant?.role || "member";
  const isAdmin = currentUserRole === "owner" || currentUserRole === "admin";
  const isOwner = currentUserRole === "owner";

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get role badge color and label
  const getRoleBadge = (role) => {
    switch (role) {
      case "owner":
        return { color: "warning", label: "Owner" };
      case "admin":
        return { color: "info", label: "Admin" };
      default:
        return { color: "secondary", label: "Member" };
    }
  };

  // Handle save group settings
  const handleSaveSettings = async () => {
    if (!editName.trim()) {
      setError("Group name is required");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateGroup(conversation.conversation_id, {
        group_name: editName.trim(),
        group_description: editDescription.trim() || null,
      });
      setIsEditing(false);
      setSuccess("Group settings updated");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update group");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle search users for adding
  const handleSearchUsers = useCallback(
    async (query) => {
      setSearchQuery(query);
      setIsSearching(true);
      await fetchChatableUsers(query);
      setIsSearching(false);
    },
    [fetchChatableUsers]
  );

  // Filter out users already in group
  const availableUsers = chatableUsers.filter(
    (u) => !groupParticipants.some((p) => p.user_id === u.user_id)
  );

  // Handle add members
  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) return;

    setIsAdding(true);
    setError(null);

    try {
      await addGroupParticipants(
        conversation.conversation_id,
        selectedUsers.map((u) => u.user_id)
      );
      setSelectedUsers([]);
      setSearchQuery("");
      setActiveTab("members");
      setSuccess(`Added ${selectedUsers.length} member(s)`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add members");
    } finally {
      setIsAdding(false);
    }
  };

  // Handle remove member
  const handleRemoveMember = async (userId) => {
    setIsRemoving(true);
    setError(null);

    try {
      await removeGroupParticipant(conversation.conversation_id, userId);
      setShowRemoveConfirm(null);
      setSuccess("Member removed");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove member");
    } finally {
      setIsRemoving(false);
    }
  };

  // Handle leave group
  const handleLeaveGroup = async () => {
    setIsLeaving(true);
    setError(null);

    try {
      await leaveGroup(conversation.conversation_id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to leave group");
      setIsLeaving(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (userId, newRole) => {
    setError(null);

    try {
      await updateParticipantRole(
        conversation.conversation_id,
        userId,
        newRole
      );
      setSuccess("Role updated");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update role");
    }
  };

  // Handle mute toggle
  const handleMuteToggle = async () => {
    try {
      await toggleGroupMute(
        conversation.conversation_id,
        !conversation.is_muted
      );
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to update notification settings"
      );
    }
  };

  // Toggle user selection for adding
  const toggleUserSelection = (user) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.user_id === user.user_id);
      if (exists) {
        return prev.filter((u) => u.user_id !== user.user_id);
      }
      return [...prev, user];
    });
  };

  return (
    <div className="group-info-panel">
      {/* Header */}
      <div className="group-info-header">
        <h6 className="m-0">Group Info</h6>
        <button className="btn btn-link text-muted p-0" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger m-2 py-2 small">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {error}
          <button
            className="btn-close btn-close-sm float-end"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success m-2 py-2 small">
          <i className="bi bi-check-circle me-1"></i>
          {success}
        </div>
      )}

      {/* Group Avatar and Name */}
      <div className="group-info-main">
        <div className="chat-avatar large group-avatar">
          <i className="bi bi-people-fill"></i>
        </div>
        {isEditing ? (
          <div className="group-edit-form">
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Group name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={100}
            />
            <textarea
              className="form-control form-control-sm mb-2"
              placeholder="Description (optional)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={2}
              maxLength={500}
            />
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary flex-grow-1"
                onClick={() => {
                  setIsEditing(false);
                  setEditName(conversation.group_name || "");
                  setEditDescription(conversation.group_description || "");
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary flex-grow-1"
                onClick={handleSaveSettings}
                disabled={isSaving || !editName.trim()}
              >
                {isSaving ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h6 className="group-info-name">{conversation.group_name}</h6>
            {conversation.group_description && (
              <p className="group-info-description">
                {conversation.group_description}
              </p>
            )}
            <div className="group-info-stats">
              <span className="text-muted">
                <i className="bi bi-people me-1"></i>
                {groupParticipants.length} members
              </span>
              {isAdmin && (
                <button
                  className="btn btn-link btn-sm p-0 ms-2"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bi bi-pencil"></i> Edit
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="group-info-tabs">
        <button
          className={`group-info-tab ${
            activeTab === "members" ? "active" : ""
          }`}
          onClick={() => setActiveTab("members")}
        >
          <i className="bi bi-people me-1"></i>
          Members
        </button>
        {isAdmin && (
          <button
            className={`group-info-tab ${activeTab === "add" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("add");
              handleSearchUsers("");
            }}
          >
            <i className="bi bi-person-plus me-1"></i>
            Add
          </button>
        )}
        <button
          className={`group-info-tab ${
            activeTab === "settings" ? "active" : ""
          }`}
          onClick={() => setActiveTab("settings")}
        >
          <i className="bi bi-gear me-1"></i>
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="group-info-content">
        {/* Members Tab */}
        {activeTab === "members" && (
          <div className="group-members-list">
            {isLoadingParticipants ? (
              <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm"></span>
              </div>
            ) : (
              groupParticipants.map((participant) => {
                const badge = getRoleBadge(participant.role);
                const isCurrentUser = participant.user_id === currentUserId;
                const canRemove =
                  isAdmin && !isCurrentUser && participant.role !== "owner";
                const canChangeRole =
                  isOwner && !isCurrentUser && participant.role !== "owner";
                const online = isUserOnline(participant.user_id);

                return (
                  <div
                    key={participant.user_id}
                    className={`group-member-item ${
                      isCurrentUser ? "is-you" : ""
                    }`}
                  >
                    <div className="avatar-with-status">
                      <div className="chat-avatar small">
                        {getInitials(participant.name)}
                      </div>
                      {online && <span className="online-dot small"></span>}
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {participant.name}
                        {isCurrentUser && (
                          <span className="text-muted ms-1">(You)</span>
                        )}
                      </div>
                      <span className={`badge bg-${badge.color} badge-sm`}>
                        {badge.label}
                      </span>
                    </div>
                    {(canRemove || canChangeRole) && (
                      <div className="member-actions dropdown">
                        <button
                          className="btn btn-link btn-sm text-muted"
                          data-bs-toggle="dropdown"
                        >
                          <i className="bi bi-three-dots-vertical"></i>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          {canChangeRole && (
                            <>
                              {participant.role === "member" ? (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      handleRoleChange(
                                        participant.user_id,
                                        "admin"
                                      )
                                    }
                                  >
                                    <i className="bi bi-shield-check me-2"></i>
                                    Make Admin
                                  </button>
                                </li>
                              ) : (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() =>
                                      handleRoleChange(
                                        participant.user_id,
                                        "member"
                                      )
                                    }
                                  >
                                    <i className="bi bi-shield-minus me-2"></i>
                                    Remove Admin
                                  </button>
                                </li>
                              )}
                            </>
                          )}
                          {canRemove && (
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() =>
                                  setShowRemoveConfirm(participant)
                                }
                              >
                                <i className="bi bi-person-x me-2"></i>
                                Remove from Group
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Add Members Tab */}
        {activeTab === "add" && (
          <div className="group-add-members">
            {/* Search */}
            <div className="add-members-search">
              <div className="chat-search-box small">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                  className="chat-search-input"
                />
              </div>
            </div>

            {/* Selected users */}
            {selectedUsers.length > 0 && (
              <div className="selected-to-add">
                <div className="selected-count-small">
                  {selectedUsers.length} selected
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAddMembers}
                  disabled={isAdding}
                >
                  {isAdding ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <>
                      <i className="bi bi-plus-lg me-1"></i>
                      Add
                    </>
                  )}
                </button>
              </div>
            )}

            {/* User list */}
            <div className="add-members-list">
              {isSearching ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm"></span>
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="text-center text-muted py-3">
                  <i className="bi bi-people d-block fs-4 mb-2"></i>
                  {searchQuery
                    ? "No users found"
                    : "All users are already in the group"}
                </div>
              ) : (
                availableUsers.map((user) => {
                  const selected = selectedUsers.some(
                    (u) => u.user_id === user.user_id
                  );
                  return (
                    <div
                      key={user.user_id}
                      className={`add-member-item ${
                        selected ? "selected" : ""
                      }`}
                      onClick={() => toggleUserSelection(user)}
                      role="button"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {}}
                        className="me-2"
                      />
                      <div className="chat-avatar tiny">
                        {getInitials(user.name)}
                      </div>
                      <div className="add-member-info">
                        <div className="add-member-name">{user.name}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="group-settings">
            {/* Notifications */}
            <div className="setting-item">
              <div className="setting-label">
                <i className="bi bi-bell me-2"></i>
                Notifications
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={!conversation.is_muted}
                  onChange={handleMuteToggle}
                />
              </div>
            </div>

            {/* Your role */}
            <div className="setting-item">
              <div className="setting-label">
                <i className="bi bi-shield me-2"></i>
                Your Role
              </div>
              <span
                className={`badge bg-${getRoleBadge(currentUserRole).color}`}
              >
                {getRoleBadge(currentUserRole).label}
              </span>
            </div>

            {/* Leave group */}
            <div className="setting-item danger">
              <button
                className="btn btn-outline-danger btn-sm w-100"
                onClick={() => setShowLeaveConfirm(true)}
              >
                <i className="bi bi-box-arrow-left me-1"></i>
                Leave Group
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div
          className="confirm-modal-overlay"
          onClick={() => setShowLeaveConfirm(false)}
        >
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h6>Leave Group?</h6>
            <p className="text-muted small">
              {isOwner
                ? "As the owner, leaving will transfer ownership to the next admin or oldest member."
                : "You will no longer receive messages from this group."}
            </p>
            <div className="confirm-actions">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowLeaveConfirm(false)}
                disabled={isLeaving}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleLeaveGroup}
                disabled={isLeaving}
              >
                {isLeaving ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Leave"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveConfirm && (
        <div
          className="confirm-modal-overlay"
          onClick={() => setShowRemoveConfirm(null)}
        >
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h6>Remove Member?</h6>
            <p className="text-muted small">
              Remove <strong>{showRemoveConfirm.name}</strong> from this group?
            </p>
            <div className="confirm-actions">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowRemoveConfirm(null)}
                disabled={isRemoving}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveMember(showRemoveConfirm.user_id)}
                disabled={isRemoving}
              >
                {isRemoving ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Remove"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupInfoPanel;
